import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, ArrowLeftRight, Syringe, ShoppingBag, Phone, ArrowRight, ShieldCheck, Clock, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO = "https://customer-assets-jt897jd0.emergentagent.net/job_meds-admin-dashboard/artifacts/wpi9i5xz_cp.front.jpg";

const SERVICES = [
  { icon: Truck, title: "Prescription Delivery", desc: "Get your refills delivered securely to your door.", to: "/delivery", cta: "Request Delivery" },
  { icon: ArrowLeftRight, title: "Transfer Rx", desc: "Move your prescriptions to us in minutes.", to: "/transfer", cta: "Start Transfer" },
  { icon: Syringe, title: "Immunizations", desc: "TDAP, Flu, Covid & Hepatitis vaccines available.", to: "/immunizations", cta: "Book Now" },
  { icon: ShoppingBag, title: "OTC Store", desc: "Reserve over-the-counter items for pickup or delivery.", to: "/otc-store", cta: "Shop OTC" },
];

const fade = { hidden: { opacity: 0, y: 24 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1 } }) };

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grain-bg">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={fade} custom={0} className="order-2 lg:order-1">
            <span className="inline-block rounded-full bg-secondary px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Raton, New Mexico
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Your health,<br /><span className="text-primary">personally</span> cared for.
            </h1>
            <p className="mt-6 max-w-lg text-base text-muted-foreground">
              Crystal Pharmacy makes managing your medications simple — fast refills, free delivery, easy transfers, and a friendly team that knows your name.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full px-7" data-testid="hero-delivery-btn">
                <Link to="/delivery">Refill & Deliver <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-call-btn">
                <a href="tel:5753957559"><Phone className="mr-2 h-4 w-4" /> 575-395-7559</a>
              </Button>
            </div>
            <div className="mt-6 inline-flex items-center gap-4 rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid="hero-hours">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary"><Clock className="h-5 w-5" /></div>
              <div className="text-sm">
                <p className="font-heading font-bold">Open Hours</p>
                <p className="text-muted-foreground">Mon–Fri 9 AM–6 PM · Sat–Sun 10 AM–5 PM</p>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Secure & private</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Same-day refills</span>
              <span className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" /> Local & trusted</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative order-1 lg:order-2">
            <div className="overflow-hidden rounded-3xl border border-border shadow-2xl">
              <img src={HERO} alt="Crystal Pharmacy storefront" className="h-auto w-full object-contain lg:h-[26rem] lg:object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="mb-10 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">What we offer</span>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight">Everything your family needs, in one place.</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="font-heading text-lg font-bold">{s.title}</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.desc}</p>
              <Link to={s.to} data-testid={`home-service-${s.to.slice(1)}`}
                className="mt-4 inline-flex items-center text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                {s.cta} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-accent px-8 py-12 text-white md:flex-row md:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold md:text-3xl">Ready to make Crystal your pharmacy?</h2>
            <p className="mt-2 max-w-xl text-teal-200">Create a patient profile to reserve OTC items, request deliveries, and manage your care.</p>
          </div>
          <Button asChild size="lg" className="rounded-full bg-white px-7 text-accent hover:bg-teal-100 hover:text-accent" data-testid="home-cta-portal">
            <Link to="/portal">Create Patient Account</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
